import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {permissions$} from 'models/auth';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/customs/grid/table';

import {UserPermission} from 'types';
import {CustomsRow} from 'types/profile';

interface Props {
    configFunc: (t: TFunction, permissions?: UserPermission[]) => Array<any>;
    dataMapper: (
        data: CustomsRow[] | undefined,
        updateCustoms: () => void,
        permissions?: UserPermission[],
    ) => CustomsRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const CustomsGridTable = ({configFunc, dataMapper, headerControls, exportConfig}: Props) => {
    const permissions = useStore(permissions$);
    const {t} = useTranslation();
    const {data, pageNumber, filters, sortings, dataLoading} = useStore(tableState$);
    useGate(Gate);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t, permissions),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );
    const mappedData = React.useMemo(
        () => dataMapper(data?.data, () => history.push(ROUTE.CUSTOMS_UPDATE), permissions),
        [dataMapper, data, t],
    );

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={data?.totalElements}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            hasNext={(data?.page ?? 0) < (data?.totalPages ?? 0)}
            isLoading={dataLoading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'customs',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            exportConfig={exportConfig}
        />
    );
};
